// src/utils/config.ts

// Environment variables
const DEV_ACCOUNTS_API_URL = import.meta.env.VITE_ACCOUNTS_API_URL;
const DEV_DEVICES_API_URL = import.meta.env.VITE_DEVICES_API_URL;
const DEV_NOTES_API_URL = import.meta.env.VITE_NOTES_API_URL;
const DEV_FIREBASE_API_URL = import.meta.env.VITE_FIREBASE_API_URL;
const DEV_FETCH_DAILY_API_URL = import.meta.env.VITE_FETCH_DAILY_API_URL;
const DEV_DOWNLOAD_CSV_API_URL = import.meta.env.VITE_DOWNLOAD_CSV_API_URL;
const DEV_SCATTER_PLOT_API_URL = import.meta.env.VITE_SCATTER_PLOT_API_URL;
const DEV_CLAIM_THING_API_URL = import.meta.env.VITE_CLAIM_THING_API_URL;
const DEV_RESET_THING_API_URL = import.meta.env.VITE_RESET_THING_API_URL;
const DEV_CHECK_ENROLLMENT_API_URL = import.meta.env.VITE_CHECK_ENROLLMENT_API_URL;
const DEV_TEMPERATURE_PLOT_API_URL = import.meta.env.VITE_TEMPERATURE_PLOT_API_URL;
const DEV_DEVICE_DEBUG_API_URL = import.meta.env.VITE_DEVICE_DEBUG_API_URL;
const DEV_FIRESTORE_QUERY_API_URL = import.meta.env.VITE_FIRESTORE_QUERY_API_URL;
const DEV_FIRMWARE_API_URL = import.meta.env.VITE_FIRMWARE_API_URL;
const DEV_FILTER_CATALOG_API_URL = import.meta.env.VITE_FILTER_CATALOG_API_URL;

const PROD_ACCOUNTS_API_URL = import.meta.env.VITE_PROD_ACCOUNTS_API_URL;
const PROD_DEVICES_API_URL = import.meta.env.VITE_PROD_DEVICES_API_URL;
const PROD_NOTES_API_URL = import.meta.env.VITE_PROD_NOTES_API_URL;
const PROD_FIREBASE_API_URL = import.meta.env.VITE_PROD_FIREBASE_API_URL;
const PROD_FETCH_DAILY_API_URL = import.meta.env.VITE_PROD_FETCH_DAILY_API_URL;
const PROD_DOWNLOAD_CSV_API_URL = import.meta.env.VITE_PROD_DOWNLOAD_CSV_API_URL;
const PROD_SCATTER_PLOT_API_URL = import.meta.env.VITE_PROD_SCATTER_PLOT_API_URL;
const PROD_CLAIM_THING_API_URL = import.meta.env.VITE_PROD_CLAIM_THING_API_URL;
const PROD_RESET_THING_API_URL = import.meta.env.VITE_PROD_RESET_THING_API_URL;
const PROD_CHECK_ENROLLMENT_API_URL = import.meta.env.VITE_PROD_CHECK_ENROLLMENT_API_URL;
const PROD_TEMPERATURE_PLOT_API_URL = import.meta.env.VITE_PROD_TEMPERATURE_PLOT_API_URL;
const PROD_DEVICE_DEBUG_API_URL = import.meta.env.VITE_PROD_DEVICE_DEBUG_API_URL;
const PROD_FIRESTORE_QUERY_API_URL = import.meta.env.VITE_PROD_FIRESTORE_QUERY_API_URL;
const PROD_FIRMWARE_API_URL = import.meta.env.VITE_PROD_FIRMWARE_API_URL;
const PROD_FILTER_CATALOG_API_URL = import.meta.env.VITE_PROD_FILTER_CATALOG_API_URL;

const isProduction = import.meta.env.PROD;

const deriveDownloadCsvApiUrl = (fetchDailyApiUrl?: string) => {
  if (!fetchDailyApiUrl) return undefined;
  const normalized = fetchDailyApiUrl.replace(/\/+$/, '');
  const analysisSuffix = '/analysis';
  if (normalized.endsWith(analysisSuffix)) {
    return `${normalized.slice(0, -analysisSuffix.length)}/download-csv`;
  }
  return `${normalized}/download-csv`;
};

const fetchDailyApiUrl = isProduction ? PROD_FETCH_DAILY_API_URL : DEV_FETCH_DAILY_API_URL;
const downloadCsvApiUrl = (isProduction ? PROD_DOWNLOAD_CSV_API_URL : DEV_DOWNLOAD_CSV_API_URL)
  || deriveDownloadCsvApiUrl(fetchDailyApiUrl);

// The cross-device read-bundle feed lives on the same API as device-debug
// (GET /bundles/recent). Derive it from the device-debug URL so it needs no
// new env var, but allow an explicit override.
const deriveRecentBundlesApiUrl = (deviceDebugApiUrl?: string) => {
  if (!deviceDebugApiUrl) return undefined;
  const normalized = deviceDebugApiUrl.replace(/\/+$/, '');
  const suffix = '/device-debug';
  if (normalized.endsWith(suffix)) {
    return `${normalized.slice(0, -suffix.length)}/bundles/recent`;
  }
  return `${normalized}/bundles/recent`;
};

const deviceDebugApiUrl = isProduction ? PROD_DEVICE_DEBUG_API_URL : DEV_DEVICE_DEBUG_API_URL;
const recentBundlesApiUrl = (isProduction
  ? import.meta.env.VITE_PROD_RECENT_BUNDLES_API_URL
  : import.meta.env.VITE_RECENT_BUNDLES_API_URL)
  || deriveRecentBundlesApiUrl(deviceDebugApiUrl);

const config = {
  accountsApiUrl: isProduction ? PROD_ACCOUNTS_API_URL : DEV_ACCOUNTS_API_URL,
  devicesApiUrl: isProduction ? PROD_DEVICES_API_URL : DEV_DEVICES_API_URL,
  notesApiUrl: isProduction ? PROD_NOTES_API_URL : DEV_NOTES_API_URL,
  firebaseApiUrl: isProduction ? PROD_FIREBASE_API_URL : DEV_FIREBASE_API_URL,
  fetchDailyApiUrl,
  downloadCsvApiUrl,
  scatterPlotApiUrl: isProduction ? PROD_SCATTER_PLOT_API_URL : DEV_SCATTER_PLOT_API_URL,
  claimThingApiUrl: isProduction ? PROD_CLAIM_THING_API_URL : DEV_CLAIM_THING_API_URL,
  resetThingApiUrl: isProduction ? PROD_RESET_THING_API_URL : DEV_RESET_THING_API_URL,
  checkEnrollmentApiUrl: isProduction ? PROD_CHECK_ENROLLMENT_API_URL : DEV_CHECK_ENROLLMENT_API_URL,
  temperaturePlotApiUrl: isProduction ? PROD_TEMPERATURE_PLOT_API_URL : DEV_TEMPERATURE_PLOT_API_URL,
  deviceDebugApiUrl,
  recentBundlesApiUrl,
  firestoreQueryApiUrl: isProduction ? PROD_FIRESTORE_QUERY_API_URL : DEV_FIRESTORE_QUERY_API_URL,
  firmwareApiUrl: isProduction ? PROD_FIRMWARE_API_URL : DEV_FIRMWARE_API_URL,
  filterCatalogApiUrl: isProduction ? PROD_FILTER_CATALOG_API_URL : DEV_FILTER_CATALOG_API_URL,
  alertsApiUrl: (isProduction ? import.meta.env.VITE_PROD_ALERTS_API_URL : import.meta.env.VITE_ALERTS_API_URL) || '',
};

export default config;
